import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Section = ({ heading, children }) => (
  <section className="space-y-2">
    {heading && (
      <h2 className="text-base font-semibold text-foreground">{heading}</h2>
    )}
    <div className="text-sm text-muted-foreground leading-relaxed space-y-2">
      {children}
    </div>
  </section>
);

const SubHeading = ({ children }) => (
  <h3 className="text-sm font-semibold text-foreground mt-3">{children}</h3>
);

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-4xl mx-auto flex h-14 items-center px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="mr-2"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Privacy Policy</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="p-6 sm:p-8" data-testid="privacy-content">
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Privacy Policy
              </h1>
              <p className="text-sm text-muted-foreground">
                Foresight Meet Schedule Predictor
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Effective Date: April 17, 2026
              </p>
              <p className="text-xs text-muted-foreground">
                Last Updated: July 4, 2026
              </p>
            </div>

            <Section heading="Introduction">
              <p>
                Foresight Meet Schedule Predictor (the &ldquo;App&rdquo;) is developed and
                operated by Foresight Pulse LLC, located in Dublin, Ohio, United States
                (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;).
              </p>
              <p>
                This Privacy Policy explains how we collect, use, store, and protect
                information when you use our App. We are committed to protecting your
                privacy and being fully transparent about our data practices.
              </p>
            </Section>

            <Section heading="1. Information We Collect">
              <p>
                We collect the minimum data necessary to provide the core functionality,
                real-time synchronization, and backup capabilities of the App.
              </p>

              <SubHeading>A. Information Associated with Account Authentication</SubHeading>
              <p>
                <span className="font-medium text-foreground">
                  Google Account Information:
                </span>{' '}
                When you choose to sign in using Google Sign-In, we receive your name,
                email address, and profile picture URL from Google via our authentication
                framework (Supabase Auth). This information is used strictly to establish
                your secure user account and verify your identity.
              </p>

              <SubHeading>B. App and Core Functionality Data</SubHeading>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <span className="font-medium text-foreground">Device Identifier:</span>{' '}
                  If you use the app without an account, we generate a random, anonymous
                  Device UUID (Universally Unique Identifier). This identifier is used to
                  associate your created meet schedules with your specific device. It
                  contains no personal information and cannot be used by us to identify
                  you personally.
                </li>
                <li>
                  <span className="font-medium text-foreground">Meet Data:</span> The
                  sports meet schedules you create (including track &amp; field, cross
                  country, and swimming meet names, venues, dates, times, and event
                  configurations) are stored on our secure cloud servers, linked either to
                  your authenticated account or your anonymous Device UUID. This enables
                  seamless syncing across sessions and devices and provides reliable
                  cloud backup.
                </li>
                <li>
                  <span className="font-medium text-foreground">Premium Status:</span> We
                  record whether you have purchased the premium upgrade (to unlock
                  unlimited meets). Payment processing is handled entirely by Apple (App
                  Store) or Google (Play Store). We do not collect, see, or store any of
                  your credit card or payment details.
                </li>
              </ul>

              <SubHeading>C. Technical and Performance Diagnostics</SubHeading>
              <p>
                <span className="font-medium text-foreground">Crash Reporting:</span> We
                use performance diagnostic tools (such as Sentry) to identify code errors.
                These tools collect anonymous technical variables (such as device model,
                operating system version, and error logs) to help us deploy bug fixes and
                maintain app stability.
              </p>

              <SubHeading>D. Information We Do Not Collect</SubHeading>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  We do not collect physical addresses, phone numbers, or any contact
                  information outside of your authenticated Google email address.
                </li>
                <li>
                  We do not collect precise or approximate real-time location data (any
                  permissions used for event configurations are handled entirely locally
                  on your device).
                </li>
                <li>
                  We do not collect photos, camera data, or personal media files (outside
                  of displaying the profile picture URL provided by your Google login
                  profile).
                </li>
                <li>
                  We do not use behavioral tracking, tracking pixels, or advertising
                  identifiers for marketing purposes.
                </li>
              </ul>
            </Section>

            <Section heading="2. How We Use Your Information">
              <p>We use the collected information solely to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Authenticate your identity and manage your secure account profiles.</li>
                <li>
                  Store and sync your meet configurations seamlessly across multiple
                  personal devices or application sessions.
                </li>
                <li>Manage and enforce your free vs. premium account tier status.</li>
                <li>
                  Provide core App functionality (creating, viewing, editing, and backing
                  up meets).
                </li>
                <li>
                  Diagnose system crashes, troubleshoot technical bugs, and improve overall
                  App performance.
                </li>
              </ul>
              <p>
                We do not sell, rent, lease, or share your personal information or email
                addresses with third parties for marketing or advertising purposes.
              </p>
            </Section>

            <Section heading="3. Data Storage, Retention, and Sharing">
              <p>
                <span className="font-medium text-foreground">Cloud Storage:</span> Your
                account profile data and meet data are housed on secure cloud servers using
                industry-standard protocols (encryption in transit and at rest, strict
                access controls). A local copy is also cached on your device to support
                offline access.
              </p>
              <p>
                <span className="font-medium text-foreground">Retention:</span> We retain
                your data only as long as needed to provide the service or until you
                request its erasure.
              </p>
              <p>
                <span className="font-medium text-foreground">Limited Disclosures:</span>{' '}
                We may disclose data only in the following restricted scenarios:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  With trusted infrastructure providers who host and operate our backend
                  services (under strict confidentiality and data protection agreements).
                </li>
                <li>If required to do so by law, court order, or a valid legal process.</li>
                <li>
                  To protect the rights, property, or safety of Foresight Pulse LLC, our
                  users, or the general public.
                </li>
              </ul>
            </Section>

            <Section heading="4. Your Rights and Choices">
              <p>
                <span className="font-medium text-foreground">
                  Account and Data Deletion:
                </span>{' '}
                You retain complete control over your data records. Uninstalling the App
                deletes all local data from your device. If you want your stored account
                profile record and your synced meet configurations permanently deleted
                from our application servers, you can initiate a deletion request
                by emailing us at{' '}
                <a
                  href="mailto:foresightpulse@gmail.com"
                  className="underline hover:text-foreground"
                >
                  foresightpulse@gmail.com
                </a>
                . We will manually purge your user record from our databases within 30
                days. This action strictly deletes your data within our application
                environment and does not affect or alter your global personal Google
                account.
              </p>
              <p>
                <span className="font-medium text-foreground">Offline Use:</span> The App
                continues to support local, offline storage functionality if you choose not
                to utilize cross-device account synchronization.
              </p>
            </Section>

            <Section heading="5. Children's Privacy">
              <p>
                The App is designed as a utility tool for coaches, event organizers,
                parents, and athletes. We do not knowingly target or collect personal
                information directly from children under the age of 13. If you believe we
                have inadvertently collected account profile data from a child under 13,
                please contact us immediately at our support email, and we will purge the
                corresponding user record from our servers immediately.
              </p>
            </Section>

            <Section heading="6. Changes to This Privacy Policy">
              <p>
                We may update this policy from time to time. We will notify you of
                significant changes by updating the &ldquo;Last Updated&rdquo; date at the
                top of this document and displaying a notice within the App. Your continued
                use of the App after changes constitutes acceptance of the updated policy.
              </p>
            </Section>

            <Section heading="Contact Us">
              <p>
                If you have any questions about this Privacy Policy or our data practices,
                please contact us:
              </p>
              <p>
                Foresight Pulse LLC
                <br />
                Dublin, Ohio, United States
                <br />
                Email:{' '}
                <a
                  href="mailto:foresightpulse@gmail.com"
                  className="underline hover:text-foreground"
                >
                  foresightpulse@gmail.com
                </a>
              </p>
            </Section>
          </div>
        </Card>
      </main>
    </div>
  );
}
